import React, { useRef } from "react";
import { FcPrint } from "react-icons/fc";
import { DateTime } from "luxon";

import {
  abrirPdfEnNavegador,
  generarPdfEstadoDeCuentaVn,
  quitarTablaAmortizacion,
} from "../../API/APIImpresionEstadoCuenta";
import { postGenerarEstadoDeCuentaAPI } from "../../API/ApiContratos";
import { AuthContext } from "../../auth/authContext";
import { LoadingComponent } from "../../Components/LoadingComponent/LoadingComponent";
import { BuscadorDeContratosParaReporteComponentEstadoDeCuenta } from "../../Components/BuscadorDeContratosParaReporteComponent/BuscadorDeContratosParaReporteComponentEstadoDeCuenta";
import SeleccionaUnContratoComponent from "../SeleccionaUnContratoComponent/SeleccionaUnContratoComponent";
import imageVitanova from "../../Images/LogotipoNegro.png";

const BRAND_COLOR = "#111111";

const fmtFecha = (iso) =>
  iso ? DateTime.fromISO(iso).toFormat("dd-MM-yyyy") : "—";

const fmtMonto = (monto) =>
  `$${parseFloat(monto || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
  })}`;

const FilaDato = ({ label, value }) => (
  <tr>
    <td style={{ fontWeight: "bold", whiteSpace: "nowrap", paddingRight: 8 }}>
      {label}
    </td>
    <td>{value}</td>
  </tr>
);

const BarraProgreso = ({ porcentajePagado, porcentajePorPagar }) => {
  const pagado = parseFloat(porcentajePagado) || 0;

  return (
    <div style={{ marginTop: 6 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.78em",
          marginBottom: 2,
        }}
      >
        <span style={{ color: "green", fontWeight: "bold" }}>
          Pagado: {porcentajePagado}
        </span>
        <span style={{ color: "#c0392b", fontWeight: "bold" }}>
          Pendiente: {porcentajePorPagar}
        </span>
      </div>
      <div
        style={{
          height: 10,
          background: "#e0e0e0",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pagado}%`,
            background: "linear-gradient(90deg, #27ae60, #2ecc71)",
            borderRadius: 6,
            transition: "width 0.4s",
          }}
        />
      </div>
    </div>
  );
};

const TablaPagos = ({ pagos, mostrarRestante = false }) => (
  <table
    className="table table-bordered table-sm"
    style={{ fontSize: "0.82em" }}
  >
    <thead style={{ background: BRAND_COLOR, color: "white" }}>
      <tr>
        <th className="text-center">Recibo</th>
        <th className="text-center">Abono</th>
        <th className="text-center">Fecha de pago</th>
        {mostrarRestante && (
          <th className="text-center">Saldo restante</th>
        )}
      </tr>
    </thead>
    <tbody>
      {pagos?.map((pago, index) => (
        <tr key={index} className="text-center">
          <td>
            {pago.serie ?? pago.cseriecobranza?.serie} - {pago.folio}
          </td>
          <td>{pago.fmMontoPago ?? fmtMonto(pago.monto)}</td>
          <td>{fmtFecha(pago.fechaPago)}</td>
          {mostrarRestante && <td>{pago.restante}</td>}
        </tr>
      ))}
    </tbody>
  </table>
);

const TablaAmortizacion = ({ tablaAmortizacion }) => (
  <table
    className="table table-bordered table-sm"
    style={{ fontSize: "0.82em" }}
  >
    <thead style={{ background: BRAND_COLOR, color: "white" }}>
      <tr>
        <th className="text-center">N° de pago</th>
        <th className="text-center">Fecha de pago</th>
        <th className="text-center">Monto</th>
        <th className="text-center">Saldo</th>
      </tr>
    </thead>
    <tbody>
      {tablaAmortizacion?.map((pago, index) => (
        <tr key={index} className="text-center">
          <td>{pago.numeroPago}</td>
          <td>{pago.fechaPago}</td>
          <td>{pago.monto}</td>
          <td>{pago.saldo}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const EstadoDeCuentaReporte = ({ finalData, printable = false }) => (
  <div
    className={`container-fluid mt-5${printable ? " d-none d-print-block" : ""}`}
    style={{ fontSize: "0.88em" }}
  >
    <div
      className="row align-items-center mb-3"
      style={{
        borderBottom: `3px solid ${BRAND_COLOR}`,
        paddingBottom: 12,
      }}
    >
      <div className="col-5 text-center">
        <img height={70} src={imageVitanova} alt="Vitanova" />
        <h2
          style={{
            fontSize: "1.3em",
            fontWeight: "bold",
            marginTop: 6,
            marginBottom: 2,
          }}
        >
          ESTADO DE CUENTA
        </h2>
        <p style={{ color: "#555", margin: 0 }}>
          Fecha:{" "}
          {new Date().toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          })}
        </p>
      </div>

      <div className="col-7">
        <table className="table table-sm mb-0" style={{ fontSize: "0.95em" }}>
          <tbody>
            <FilaDato
              label="Contrato:"
              value={`${finalData?.serie}-${finalData?.noContrato}`}
            />
            <FilaDato label="Titular:" value={finalData?.titular} />
            <FilaDato
              label="Domicilio:"
              value={`${finalData?.domicilio ?? ""} ${finalData?.colonia ?? ""}, CP: ${
                finalData?.cp ?? ""
              }`}
            />
            <FilaDato
              label="Servicio:"
              value={finalData?.detalleContrato?.servicio?.nombre}
            />
            <FilaDato
              label="Serie gestor:"
              value={
                finalData?.detalleContrato?.detalleContrato == null
                  ? "SIN SERIE"
                  : finalData?.detalleContrato?.detalleContrato?.serieGestor
              }
            />
          </tbody>
        </table>
      </div>
    </div>

    <div className="row mb-3">
      <div className="col-12">
        <div
          style={{
            background: BRAND_COLOR,
            color: "white",
            padding: "4px 10px",
            fontWeight: "bold",
            borderRadius: "4px 4px 0 0",
          }}
        >
          RESUMEN FINANCIERO
        </div>
        <table
          className="table table-bordered table-sm mb-0"
          style={{ borderRadius: "0 0 4px 4px" }}
        >
          <tbody>
            <tr>
              <td>
                <strong>Fecha del contrato:</strong>{" "}
                {fmtFecha(finalData?.fechaContrato)}
              </td>
              <td>
                <strong>Financiamiento:</strong>{" "}
                {finalData?.detalleContrato?.plazo} meses
              </td>
              <td>
                <strong>Pagos realizados:</strong> {finalData?.pagos?.length}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Costo total:</strong> {finalData?.importeContrato}
              </td>
              <td>
                <strong>Abono a capital:</strong> {finalData?.pagadoHoy}
              </td>
              <td>
                <strong>Saldo pendiente:</strong> {finalData?.porPagar}
              </td>
            </tr>
            {finalData?.pagosDeRestructura && (
              <tr>
                <td colSpan="3">
                  <strong>Pagos de restructura aplicados:</strong>{" "}
                  {finalData?.pagosDeRestructura}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div
          style={{
            border: "1px solid #dee2e6",
            borderTop: "none",
            padding: "8px 10px",
            borderRadius: "0 0 4px 4px",
          }}
        >
          <BarraProgreso
            porcentajePagado={finalData?.porcentajePagado}
            porcentajePorPagar={finalData?.porcentajePorPagar}
          />
        </div>

        <div
          style={{
            background: "#fdecea",
            border: "1px solid #f5c6c2",
            borderRadius: 4,
            padding: "5px 10px",
            marginTop: 6,
            textAlign: "center",
            color: "#c0392b",
            fontWeight: "bold",
          }}
        >
          Monto en atraso: {finalData?.montoAtraso}
        </div>
      </div>
    </div>

    <div className="row">
      <div className="col-6">
        <img
          src="https://funerariavitanova.com/ads/estadodecuenta/imagenDerecha.jpg"
          alt="Contrato reestructurado"
          style={{ width: "100%" }}
        />
      </div>
      <div className="col-6">
        <img
          src="https://funerariavitanova.com/ads/estadodecuenta/imagenIzquierda.jpg"
          alt="Contrato reestructurado"
          style={{ width: "100%" }}
        />
      </div>
    </div>

    <div className="row mb-3">
      <div className="col-12">
        <div
          style={{
            background: BRAND_COLOR,
            color: "white",
            padding: "4px 10px",
            fontWeight: "bold",
            borderRadius: "4px 4px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            MOVIMIENTOS - CONTRATO {finalData?.serie}-{finalData?.noContrato}
          </span>
        </div>
        <TablaPagos pagos={finalData?.pagos} mostrarRestante={true} />
      </div>
    </div>

    {finalData?.tabla_amortizacion && finalData?.tabla_amortizacion?.length > 0 && (
      <div className="row mb-3">
        <div className="col-12">
          <div
            style={{
              background: BRAND_COLOR,
              color: "white",
              padding: "4px 10px",
              fontWeight: "bold",
              borderRadius: "4px 4px 0 0",
            }}
          >
            TABLA DE AMORTIZACIÓN
          </div>
          <TablaAmortizacion tablaAmortizacion={finalData?.tabla_amortizacion} />
        </div>
      </div>
    )}

    {finalData?.detalleRestructura?.length > 0 && (
      <span
        style={{
          background: "#000000",
          color: "white",
          borderRadius: 4,
          padding: "1px 8px",
          fontSize: "0.85em",
          fontWeight: "bold",
          letterSpacing: "0.5px",
        }}
      >
        CONTRATO RESTRUCTURADO
      </span>
    )}

    {/* finalData?.detalleRestructura?.length > 0 && (
      <div className="row">
        <div className="col-12">
          <div
            style={{
              background: "#555",
              color: "white",
              padding: "4px 10px",
              fontWeight: "bold",
              borderRadius: "4px 4px 0 0",
            }}
          >
            HISTORIAL DE CONTRATOS ANTERIORES (RESTRUCTURA)
          </div>

          {finalData.detalleRestructura.map((contrato, index) => {
            const totalContrato =
              contrato.pagos?.reduce(
                (sum, pago) => sum + parseFloat(pago.monto || 0),
                0
              ) ?? 0;

            return (
              <div
                key={index}
                style={{
                  border: "1px solid #bbb",
                  marginBottom: 12,
                  borderRadius: 4,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    background: "#f0f0f0",
                    color: "#222",
                    padding: "6px 10px",
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    fontSize: "0.9em",
                    borderBottom: "2px solid #bbb",
                  }}
                >
                  <span>
                    <strong>Contrato:</strong> {contrato.serie}-
                    {contrato.noContrato}
                  </span>
                  <span>
                    <strong>Titular:</strong> {contrato.titular}
                  </span>
                  <span>
                    <strong>Fecha:</strong> {fmtFecha(contrato.fechaContrato)}
                  </span>
                  <span
                    style={{
                      marginLeft: "auto",
                      color: "#27ae60",
                      fontWeight: "bold",
                    }}
                  >
                    <strong>Total pagado:</strong> {fmtMonto(totalContrato)}
                  </span>
                </div>

                <TablaPagos pagos={contrato.pagos} mostrarRestante={false} />
              </div>
            );
          })}
        </div>
      </div>
    ) */}
  </div>
);

export const EstadoDeCuentaContratosDosScreenComponent = () => {
  const printRef = useRef();
  const { user } = React.useContext(AuthContext);

  const [datosdeContrato, setDatosDeContrato] = React.useState({
    etapaUno: [],
    etapaDos: [],
  });
  const [numeroContratoEncontrado, setNumeroContratoEncontrado] =
    React.useState(0);
  const [finalData, setFinalData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = React.useState(false);
  const [mostrarRecibo, setMostrarRecibo] = React.useState(false);

  const fetchEstadoDeCuenta = React.useCallback(
    async (idcontrato) => {
      if (!idcontrato) {
        setFinalData(null);
        setMostrarRecibo(false);
        return null;
      }

      setLoading(true);

      try {
        const response = await postGenerarEstadoDeCuentaAPI(user, {
          idcontrato,
        });

        if (response.status === 200) {
          setFinalData(response.data);
          setMostrarRecibo(true);
          return response.data;
        }

        setFinalData(null);
        setMostrarRecibo(false);
        alert("No se pudo obtener la información del estado de cuenta.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  React.useEffect(() => {
    fetchEstadoDeCuenta(numeroContratoEncontrado);
  }, [fetchEstadoDeCuenta, numeroContratoEncontrado]);

  const handlePrintWithData = async () => {
    setIsGeneratingPdf(true);

    try {
      const data = await fetchEstadoDeCuenta(numeroContratoEncontrado);

      if (data) {
        const incluirTablaAmortizacion = window.confirm(
          "¿Desea incluir la tabla de amortización en el estado de cuenta?"
        );
        const datosImpresion = incluirTablaAmortizacion
          ? data
          : quitarTablaAmortizacion(data);

        const pdfResponse = await generarPdfEstadoDeCuentaVn(datosImpresion);
        abrirPdfEnNavegador(pdfResponse);
      }
    } catch (error) {
      console.error(error);
      alert("No se pudo generar el PDF del estado de cuenta.");
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <>
      {isGeneratingPdf ? (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(255, 255, 255, 0.78)",
            backdropFilter: "blur(2px)",
            zIndex: 2000,
          }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-warning mb-3"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Generando PDF...</span>
            </div>
            <h5 className="mb-1">Generando PDF</h5>
            <p className="mb-0 text-muted">Espera un momento, se abrirá automáticamente.</p>
          </div>
        </div>
      ) : null}

      <div className="container-fluid d-print-none">
        <div className="row">
          <div className="col-12 mt-3">
            <BuscadorDeContratosParaReporteComponentEstadoDeCuenta
              numeroContratoEncontrado={numeroContratoEncontrado}
              setNumeroContratoEncontrado={setNumeroContratoEncontrado}
              datosdeContrato={datosdeContrato}
              setDatosDeContrato={setDatosDeContrato}
            />
          </div>
        </div>
      </div>

      {mostrarRecibo && finalData ? (
        <>
          <div className="container-fluid d-print-none">
            <div className="row text-end">
              <div className="col-sm-12 mt-4">
                <button
                  className="btn btn-warning ms-1"
                  disabled={isGeneratingPdf}
                  onClick={handlePrintWithData}
                >
                  {isGeneratingPdf ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Generando PDF...
                    </>
                  ) : (
                    <>
                      <FcPrint /> Imprimir Estado de Cuenta
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <EstadoDeCuentaReporte finalData={finalData} />
          <div ref={printRef}>
            <EstadoDeCuentaReporte finalData={finalData} printable={true} />
          </div>
        </>
      ) : (
        <SeleccionaUnContratoComponent />
      )}
    </>
  );
};
